'use client';

import './sac-card.scss';

import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.js';

import { Image } from '../../atoms/image/image';

const getBirthday = (date: string) => {
  const dateFormat = new Date(date);

  // TODO: get local from browser or translations?
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'utc',
  }).format(dateFormat);
};

type SacCardProps = ChildAvailabilityItem & {
  selectedChild?: string;
  setSelectedChild?: (childId: string) => void;
};

export const SacCard: React.FC<SacCardProps> = ({
  name: { preferred: preferredName },
  imageUrl,
  birthDate,
  icp: { country },
  minDaysWaiting,
  age,
  globalId,
  sourceCode,
  selectedChild,
  setSelectedChild,
}) => {
  const { t } = useTranslation();
  const router = useRouter();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setSelectedChild?.(globalId);

    e.preventDefault();
    router.push(
      `/sponsor-a-child/child-bio?gid=${globalId}&source=${sourceCode}`,
    );
    // Force refresh since layout doesn’t re-render on searchParams change under Suspense.
    // TODO: Revisit once layout handles this properly.
    router.refresh();
  };

  const imageURLTransform =
    imageUrl?.replace('w_150', 'f_auto,q_auto,c_scale,w_500') || undefined;

  return (
    <div className="flex flex-col relative">
      <Link
        href={`/sponsor-a-child/child-bio?gid=${globalId}&source=${sourceCode}`}
        className="SacCard__bio-link"
        onClick={handleClick}
        title={`${t('skippy.sac.bio')} ${globalId})`}
      >
        <div className="relative">
          <div className="SacCard__badge absolute m-comfy-xs">
            <Badge
              color="brand"
              label={t('skippy.child.waiting', { numDays: minDaysWaiting })}
            />
          </div>
          <div className="SacCard__child-image-container">
            <Image
              image={
                imageURLTransform
                  ? { url: imageURLTransform, alt: preferredName }
                  : undefined
              }
              className="SacCard__child-image"
              rounded="lg"
              hasPlaceholder
            />
          </div>
          {selectedChild === globalId && (
            <div className="SacCard__overlay rounded-lg flex flex-col">
              <div className="SacCard__spinner mb-comfy-sm" />
              <span className="body body-md SacCard__overlay-text">
                {t('skippy.child.loading')}
              </span>
            </div>
          )}
        </div>
        <div className="grid gap-comfy-sm pt-comfy-lg lg:pt-comfy-xl">
          <span className="SacCard__name heading heading-sm">
            {preferredName}
          </span>
          <div className="SacCard__list grid gap-snug-xl lg:gap-comfy-xs body body-md">
            <span className=" SacCard__glyph-text flex gap-comfy-xs items-center">
              <ArkIcon iconName="birthday-cake" />
              {t('skippy.sac.age', {
                count: Number(age),
                birthday: getBirthday(birthDate),
              })}
            </span>
            {country && (
              <span className="SacCard__glyph-text flex gap-comfy-xs items-center">
                <ArkIcon iconName="globe-05" />
                {country}
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export const SacCardSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col">
      <div className="relative">
        <div className="absolute m-comfy-xs z-10">
          <Skeleton height={24} width={100} borderRadius="9999px" />
        </div>

        <Skeleton
          width="100%"
          height="auto"
          style={{ aspectRatio: '3/4' }}
          borderRadius="1em"
        />
      </div>

      <div className="grid gap-comfy-sm pt-comfy-lg lg:pt-comfy-xl">
        <span className="SacCard__name">
          <Skeleton height={24} width="70%" />
        </span>

        <div className="SacCard__list grid gap-snug-xl lg:gap-comfy-xs">
          <div className="SacCard__glyph-text flex gap-comfy-xs items-center">
            <Skeleton height={20} width={20} borderRadius="0.25rem" />
            <Skeleton height={20} width={180} />
          </div>
          <div className="SacCard__glyph-text flex gap-comfy-xs items-center">
            <Skeleton height={20} width={20} borderRadius="0.25rem" />
            <Skeleton height={20} width={150} />
          </div>
        </div>
      </div>
    </div>
  );
};
